import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Crown, Star } from "lucide-react";
import { useCountryDetection } from "@/hooks/useCountryDetection";
import { Skeleton } from "@/components/ui/skeleton";

const getPlans = (isIndian: boolean) => [
  {
    name: "Free Trial",
    price: isIndian ? "₹0" : "$0",
    description: "Try it out, no card needed",
    features: [
      "3 personalized stories",
      "All story themes",
      "High-quality audio",
      "Works on all devices",
    ],
    cta: "Start Free",
    highlighted: false,
    icon: Star,
    gradient: "from-[hsl(var(--playful-teal))] to-[hsl(174,72%,45%)]",
    bgColor: "bg-[hsl(var(--playful-teal),0.1)]",
    borderColor: "border-[hsl(var(--playful-teal),0.3)]",
  },
  {
    name: "Basic",
    price: isIndian ? "₹149" : "$3.99",
    period: "/month",
    description: "For regular story time",
    features: [
      `${isIndian ? "20" : "50"} stories per month`,
      "All story themes",
      "High-quality audio",
      "Cancel anytime",
    ],
    cta: "Get Started",
    highlighted: false,
    icon: Sparkles,
    gradient: "from-[hsl(var(--playful-pink))] to-[hsl(var(--playful-purple))]",
    bgColor: "bg-[hsl(var(--playful-pink),0.1)]",
    borderColor: "border-[hsl(var(--playful-pink),0.3)]",
  },
  {
    name: "Premium",
    price: isIndian ? "₹279" : "$5.99",
    period: "/month",
    description: "Best for story-loving families",
    features: [
      `${isIndian ? "50" : "100"} stories per month`,
      "All story themes",
      "Download stories",
      "Child as main character",
      "Custom story elements",
      "Priority support",
    ],
    cta: "Go Premium",
    highlighted: true,
    icon: Crown,
    gradient: "from-[hsl(var(--playful-yellow))] to-[hsl(var(--playful-orange))]",
    bgColor: "bg-gradient-to-br from-[hsl(var(--playful-yellow),0.2)] to-[hsl(var(--playful-orange),0.2)]",
    borderColor: "border-[hsl(var(--playful-yellow))]",
  },
];

const PricingSection = () => {
  const { isIndian, isLoading } = useCountryDetection();
  const plans = getPlans(isIndian);
  return (
    <section id="pricing" className="py-16 md:py-24 px-4 bg-gradient-to-b from-[hsl(var(--playful-teal),0.05)] to-background">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 bg-[hsl(var(--playful-orange),0.2)] text-[hsl(var(--playful-orange))] rounded-full text-sm font-medium mb-4">
            Simple Pricing
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Start Free,{" "}
            <span className="bg-gradient-to-r from-[hsl(var(--playful-orange))] to-[hsl(var(--playful-yellow))] bg-clip-text text-transparent">
              Upgrade Anytime
            </span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            No hidden fees. No ads. Just magical stories for your little one.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-start">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative ${plan.bgColor} rounded-3xl p-6 md:p-8 border-2 ${plan.borderColor} transition-all duration-300 hover:shadow-xl ${
                plan.highlighted ? "md:scale-105 shadow-lg" : "hover:scale-[1.02]"
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className={`bg-gradient-to-r ${plan.gradient} text-white text-sm font-bold px-4 py-1 rounded-full shadow-md`}>
                    ⭐ Most Popular
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 bg-gradient-to-br ${plan.gradient} rounded-xl flex items-center justify-center shadow-md`}>
                  <plan.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold text-foreground">
                    {plan.name}
                  </h3>
                  <p className="text-xs text-muted-foreground">{plan.description}</p>
                </div>
              </div>

              <div className="mb-6">
                <span className="font-display text-4xl font-bold text-foreground">{plan.price}</span>
                {plan.period && <span className="text-muted-foreground">{plan.period}</span>}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <div className={`w-5 h-5 bg-gradient-to-br ${plan.gradient} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/auth" className="block">
                <Button
                  className={`w-full h-12 rounded-xl font-semibold transition-all ${
                    plan.highlighted
                      ? `bg-gradient-to-r ${plan.gradient} text-white hover:opacity-90 shadow-md`
                      : "bg-card border-2 border-border text-foreground hover:bg-muted"
                  }`}
                >
                  {plan.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>

        <div className="text-center mt-8">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mb-4">
            <span className="flex items-center gap-1">✅ Cancel anytime</span>
            <span className="flex items-center gap-1">🚫 No ads ever</span>
            <span className="flex items-center gap-1">🔒 Secure payment</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
